/* Overview — the agency's landing page, the counterpart of the console's live
 * ops board.
 *
 * TWO STATES (owner, 2026-10-23: "it should not be empty").
 *
 *   SETTING UP — any of the five dispatch conditions is not met. The page leads
 *   with "Can requests reach you?": approval, services, area, papers, and the
 *   engine's own per-service answer (agency_dispatch_eligibility, recomputed on
 *   read). An empty Requests page looks the same whether nobody needs care or
 *   the agency is invisible to dispatch, so this is the first thing to fix.
 *
 *   RUNNING — all five are met. The checklist has done its job and is not
 *   shown at all (owner, 2026-10-23: "what is the sense of this section" once
 *   it is complete). It comes back by itself the day a condition lapses — an
 *   expired paper stops dispatch — because the page then is in SETTING UP
 *   again. Only papers that block dispatch keep the page in setup; papers the
 *   platform wants but does not stop work for are one line under attention.
 *   The page is the agency's day:
 *     - Live jobs (owner, 2026-10-23: "i want the live jobs section on there"):
 *       every won job with someone on it, under way first — who is at work
 *       now and since when, or who goes next and when. From the overview's own
 *       `live` list, not GET /agency/live, which logs a PHI view per read and
 *       would log one every poll; no patient here.
 *     - Today & tomorrow: the work already won, by time, and who is going
 *       (GET /agency/overview — never the patient).
 *     - Needs your attention: everything waiting on a person, in one list —
 *       jobs to staff (and those past their staffing deadline), a staff member
 *       who has not accepted a job starting soon, a decline or a lapsed offer,
 *       papers expiring within 30 days, papers still to upload, people asking
 *       to join, a service dispatch cannot send, nobody on duty.
 *     - Your team right now: who is on duty, per kind of work the agency
 *       offers — a request it has nobody to send is a request it should not
 *       accept, and it should know before the chime.
 *     - This week: what became of the requests that reached it (accepted,
 *       taken by someone faster, missed, declined) and the work completed.
 *     - Recent activity: the last things the platform told it (the bell).
 *   When nothing is open, one calm line says so and who is on duty, instead of
 *   a page of zeros. */

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCompliance,
  getCoverage,
  getEarnings,
  getOverview,
  getPrices,
  getTeam,
  listNotices,
  listOffers,
  offerPrice,
  type Compliance,
  type CoverageSetup,
  type DocumentRow,
  type LiveJob,
  type StaffMember,
  type UpcomingJob,
} from '../data/agency';
import { countdown, dateOnly, money, secondsLeft } from '../data/format';
import { jobName, roleName, serviceName } from '../data/words';
import { ago, KIND_ROUTE } from '../shell/Alerts';
import { useProfile } from '../shell/profile';
import { AllClear, Btn, Card, CardHead, CellStack, Empty, Kpi, KpiGrid, Note, Stack, Table, Tag, TRow, uiCss } from '../ui';
import { ServiceDot, useAsync, useNow } from './common';

const BLOCKED_WORDS: Record<string, string> = {
  AGENCY_NOT_ACTIVE: 'the agency is not approved yet',
  MISSING_DOCUMENT: 'a required paper is missing',
  EXPIRED_DOCUMENT: 'a required paper has expired',
  NO_SERVICE_AREA: 'no service area is set',
  SERVICE_NOT_OFFERED: 'the service is not switched on',
  SERVICE_NOT_IN_CITY: 'the platform does not run this service in your city',
};

const CONSULT_WORD: Record<string, string> = { HOME_VISIT: 'home visit', VIDEO: 'video call', AUDIO: 'phone call', CHAT: 'chat' };

/** Papers expiring within this many days are listed under "Needs your attention". */
const EXPIRY_WARN_DAYS = 30;
/** A job starting within this many hours whose person has not accepted is listed. */
const UNACCEPTED_WARN_HOURS = 3;

type Check = { ok: boolean; title: string; detail: string; to: string; action: string };

function checks(status: string, cov: CoverageSetup | null, comp: Compliance | null): Check[] {
  const services = cov ? cov.services.filter((s) => s.active) : [];
  // Only the papers dispatch waits for decide this step (blocks_dispatch). A
  // paper the platform wants but does not stop work for — a PAN, a bank proof —
  // is listed under "Needs your attention" instead. CURRENT is the server's word
  // for a paper in force (onboarding/readiness_view.go).
  const missing = comp ? missingPapers(comp).filter((c) => c.blocks_dispatch) : [];
  // Only the services the agency switched on: the cache holds a row for every
  // service, and "not offered" for one it never meant to offer is not news.
  const on = new Set(services.map((s) => s.code));
  const mine = comp ? comp.eligibility.filter((e) => !e.equipment_type_code && on.has(e.service_code)) : [];
  const ready = mine.filter((e) => e.is_dispatchable);
  const blocked = mine.filter((e) => !e.is_dispatchable);
  return [
    {
      ok: status === 'ACTIVE',
      title: 'Approved by the platform',
      detail: status === 'ACTIVE' ? 'Your agency is approved.' : 'The platform team checks every agency before sending it work.',
      to: '/documents',
      action: 'See approval',
    },
    {
      ok: services.length > 0,
      title: 'Services switched on',
      detail: cov ? (services.length ? services.map((s) => s.name).join(', ') : 'No service is switched on yet.') : 'Reading…',
      to: '/coverage',
      action: 'Choose services',
    },
    {
      ok: cov ? cov.areas.length > 0 : false,
      title: 'Service area set',
      detail: cov
        ? cov.areas.length
          ? cov.areas.map((a) => `${a.label} (${a.radius_km} km)`).join(', ')
          : 'Requests reach you only inside an area you draw.'
        : 'Reading…',
      to: '/coverage',
      action: 'Set your area',
    },
    {
      ok: comp ? missing.length === 0 : false,
      title: 'Papers dispatch needs are approved',
      detail: comp
        ? missing.length
          ? `${missing.length} still needed or waiting for review.`
          : 'Every paper that stops requests is approved.'
        : 'Reading…',
      to: '/documents',
      action: 'Upload papers',
    },
    {
      ok: ready.length > 0,
      title: 'Dispatch can send you requests',
      detail: comp
        ? ready.length
          ? `For ${ready.map((e) => serviceName(e.service_code)).join(', ')}.` +
            (blocked.length ? ` Not yet for ${blocked.map((e) => serviceName(e.service_code)).join(', ')}.` : '')
          : blocked.length
            ? blocked.map((e) => `${serviceName(e.service_code)}: ${BLOCKED_WORDS[e.blocked_reason ?? ''] ?? (e.blocked_reason ?? 'not ready').toLowerCase().replace(/_/g, ' ')}`).join(' · ')
            : 'Nothing to check until services and an area are set.'
        : 'Reading…',
      to: '/documents',
      action: 'See why',
    },
  ];
}

/** Mandatory checklist papers that are not in force. */
function missingPapers(comp: Compliance) {
  return comp.checklist.filter((c) => c.trigger_met && c.is_mandatory && c.document.state !== 'CURRENT');
}

/* ------------------------------------------------------------------ layout
 * Cards side by side are as tall as the tallest one in their row (owner,
 * 2026-10-23: no empty space under a short card). The row stretches each card;
 * FILL lets the card's body take that height, and a short message — nothing
 * booked, all clear — sits in the middle of the space instead of at its top. */
const FILL = { display: 'flex', flexDirection: 'column' } as const;
function Centered({ children }: { children: ReactNode }) {
  return <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>{children}</div>;
}

/* ------------------------------------------------------------------ helpers */

const IST = 'Asia/Kolkata';
function istDayKey(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: IST });
}
/** "Today 09:00", "Tomorrow 08:30", or a date for anything else. */
function whenWords(iso: string | null, now: number): string {
  if (!iso) return 'Time not set';
  const d = new Date(iso);
  const time = d.toLocaleTimeString('en-GB', { timeZone: IST, hour: '2-digit', minute: '2-digit' });
  const today = istDayKey(new Date(now));
  const tomorrow = istDayKey(new Date(now + 24 * 3600_000));
  const key = istDayKey(d);
  if (d.getTime() <= now) return `Under way since ${time}`;
  if (key === today) return `Today ${time}`;
  if (key === tomorrow) return `Tomorrow ${time}`;
  return `${dateOnly(iso)} ${time}`;
}

/** A live job's time: "At work since 09:05" once someone has started,
 *  otherwise when its next shift starts. */
function liveWhen(j: LiveJob, now: number): string {
  const started = j.at_work.map((p) => p.started_at).filter((s): s is string => !!s).sort()[0];
  if (started) {
    const t = new Date(started).toLocaleTimeString('en-GB', { timeZone: IST, hour: '2-digit', minute: '2-digit' });
    return istDayKey(new Date(started)) === istDayKey(new Date(now)) ? `At work since ${t}` : `At work since ${dateOnly(started)}`;
  }
  if (j.item_status === 'IN_PROGRESS') return 'Under way';
  return j.next_start_utc ? whenWords(j.next_start_utc, now) : 'Time not set';
}

/** The kinds of people the agency's switched-on services need. */
const PEOPLE_FOR: { key: string; label: string; types: string[]; services: string[] }[] = [
  { key: 'clinical', label: 'Nurses, attendants & physiotherapists', types: ['CLINICAL', 'CARE'], services: ['NURSE', 'HOME_ICU', 'PHYSIO'] },
  { key: 'doctor', label: 'Doctors', types: ['DOCTOR'], services: ['DOCTOR'] },
  { key: 'driver', label: 'Ambulance drivers & crew', types: ['LOGISTICS'], services: ['AMBULANCE'] },
];

type Attention = { key: string; tone: 'danger' | 'amber'; text: string; sub?: string; action: string; to: string };

function paperAttention(comp: Compliance, now: number): Attention[] {
  const names = new Map(comp.document_types.map((t) => [t.code, t.name]));
  const out: Attention[] = [];
  const consider = (docs: DocumentRow[], owner: string) => {
    // The newest approved paper per type is the one in force.
    const latest = new Map<string, DocumentRow>();
    for (const d of docs) {
      if (d.status !== 'APPROVED' || !d.expires_on) continue;
      const prev = latest.get(d.document_type_code);
      if (!prev || (prev.expires_on ?? '') < d.expires_on) latest.set(d.document_type_code, d);
    }
    for (const d of latest.values()) {
      const exp = new Date(`${d.expires_on}T23:59:59+05:30`).getTime();
      const days = Math.floor((exp - now) / 86_400_000);
      if (days > EXPIRY_WARN_DAYS) continue;
      const name = names.get(d.document_type_code) ?? d.document_type_code.toLowerCase().replace(/_/g, ' ');
      out.push({
        key: `paper:${owner}:${d.public_id}`,
        tone: days < 7 ? 'danger' : 'amber',
        text: days < 0 ? `${name}${owner ? ` (${owner})` : ''} has expired` : `${name}${owner ? ` (${owner})` : ''} expires in ${days} day${days === 1 ? '' : 's'}`,
        sub: `${dateOnly(d.expires_on)} — an expired paper stops requests reaching you.`,
        action: 'Upload',
        to: '/documents',
      });
    }
  };
  consider(comp.documents, '');
  for (const v of comp.vehicles) consider(v.documents, v.registration_number);
  return out;
}

/* ------------------------------------------------------------------ the page */

export function OverviewScreen() {
  const { profile } = useProfile();
  const navigate = useNavigate();
  const now = useNow();
  const offers = useAsync(listOffers, [], 15_000);
  const cov = useAsync(getCoverage, []);
  const comp = useAsync(getCompliance, []);
  const money$ = useAsync(getEarnings, []);
  const ov = useAsync(getOverview, [], 30_000);
  const team = useAsync(getTeam, [], 60_000);
  const notices = useAsync(listNotices, [], 30_000);
  const prices = useAsync(getPrices, [], 60_000);
  const c = profile.counts;
  const list = checks(profile.agency.status, cov.data, comp.data);
  const done = list.filter((x) => x.ok).length;
  // Running once every condition holds. Until the reads land, keep the setup
  // view only if it is already known to be incomplete — so a set-up agency's
  // page does not flash the checklist on every load.
  const loaded = !!cov.data && !!comp.data;
  const running = loaded && done === list.length;
  const soonest = [...(offers.data ?? [])].sort((a, b) => a.expires_at.localeCompare(b.expires_at)).slice(0, 5);
  const COLS = '1.5fr 1.2fr 0.9fr 0.8fr';

  const kpis = (
    <KpiGrid>
      <Kpi label="Requests open" value={c.open_offers} note="answer before the timer runs out" onClick={() => navigate('/requests')} color={c.open_offers ? 'var(--teal)' : undefined} />
      <Kpi label="To assign" value={c.to_staff} note="jobs you won with nobody on them" onClick={() => navigate('/assign')} color={c.to_staff ? 'var(--amber)' : undefined} />
      {/* overdue_to_staff (000079): the part of "To assign" past its staffing
          deadline — the platform has flagged it and told you (AGENCY_UNSTAFFED). */}
      <Kpi
        label="Won, nobody assigned"
        value={c.overdue_to_staff ?? '—'}
        note="past the time to staff them: assign someone now"
        onClick={() => navigate('/assign')}
        color={c.overdue_to_staff ? 'var(--danger)' : undefined}
      />
      <Kpi label="At work now" value={c.live_jobs} note="jobs your people are on" onClick={() => navigate('/live')} />
      <Kpi label="Asking to join" value={c.join_requests} note="confirm the people who work for you" onClick={() => navigate('/team')} color={c.join_requests ? 'var(--amber)' : undefined} />
      <Kpi
        label="Owed to you"
        value={money$.data ? money(money$.data.summary.accrued) : '—'}
        note={money$.data ? `${money(money$.data.summary.this_month)} earned this month` : 'for completed work'}
        onClick={() => navigate('/earnings')}
      />
    </KpiGrid>
  );

  const checklist = (
    <div>
      {list.map((x) => (
        <div key={x.title} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderTop: '1px solid var(--line-soft)' }}>
          <span
            aria-hidden="true"
            style={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              flex: 'none',
              font: '800 13px var(--font-ui)',
              background: x.ok ? 'var(--ok-bg)' : 'var(--amber-bg)',
              color: x.ok ? 'var(--ok)' : 'var(--amber)',
            }}
          >
            {x.ok ? '✓' : '!'}
          </span>
          <span className={uiCss.grow}>
            <span style={{ display: 'block', font: '700 13.5px var(--font-ui)', color: 'var(--ink)' }}>{x.title}</span>
            <span className={uiCss.sub}>{x.detail}</span>
          </span>
          {!x.ok ? (
            <Btn size="sm" onClick={() => navigate(x.to)}>
              {x.action}
            </Btn>
          ) : null}
        </div>
      ))}
    </div>
  );

  const closingSoonest = (
    <Card style={FILL}>
      <CardHead
        title="Closing soonest"
        sub="Open requests, by the time they have left."
        right={
          <Btn size="sm" onClick={() => navigate('/requests')}>
            All requests
          </Btn>
        }
      />
      {offers.error ? (
        <div style={{ padding: '0 18px 18px' }}>
          <Note tone="danger">The requests could not be read: {offers.error}</Note>
        </div>
      ) : soonest.length === 0 ? (
        <Centered><Empty>{offers.loading ? 'Reading…' : 'No request is open right now.'}</Empty></Centered>
      ) : (
        <Table cols={COLS} head={['Request', 'Area', 'Pays', 'Left']} minWidth={420}>
          {soonest.map((o) => (
            <TRow key={o.public_id} cols={COLS} onClick={() => navigate('/requests')}>
              <CellStack
                top={
                  <span style={{ font: '700 13px var(--font-ui)' }}>
                    <ServiceDot code={o.item.service_code} />
                    {jobName(o.item.service_code, o.item.variant_code)}
                  </span>
                }
                sub={o.item.is_immediate ? 'needed now' : undefined}
              />
              <span className={uiCss.sub}>{[o.item.service_city, o.item.service_pincode].filter(Boolean).join(' · ') || '—'}</span>
              <span className={uiCss.num} style={{ textAlign: 'left' }}>
                {offerPrice(o) ? money(offerPrice(o)) : 'No price yet'}
              </span>
              <span style={{ font: '700 12.5px var(--font-mono)', color: secondsLeft(o.expires_at, now) < 120 ? 'var(--danger)' : 'var(--ink)' }}>
                {countdown(o.expires_at, now)}
              </span>
            </TRow>
          ))}
        </Table>
      )}
    </Card>
  );

  /* ---------------------------------------------------------- setting up */
  if (!running) {
    return (
      <Stack>
        {kpis}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', gap: 14, alignItems: 'stretch' }}>
          <Card style={FILL}>
            <CardHead
              title="Can requests reach you?"
              sub="Dispatch offers a request to your agency only when all five are true."
              right={<Tag fg={done === list.length ? 'var(--ok)' : 'var(--amber)'} bg={done === list.length ? 'var(--ok-bg)' : 'var(--amber-bg)'}>{done} of {list.length}</Tag>}
            />
            {comp.error ? (
              <div style={{ padding: '0 18px 12px' }}>
                <Note tone="danger">Your papers could not be read: {comp.error}</Note>
              </div>
            ) : null}
            {checklist}
          </Card>
          {closingSoonest}
        </div>
      </Stack>
    );
  }

  /* ---------------------------------------------------------- running */
  const activeServices = (cov.data?.services ?? []).filter((s) => s.active).map((s) => s.code);
  const blockedServices = (comp.data?.eligibility ?? []).filter((e) => !e.equipment_type_code && activeServices.includes(e.service_code) && !e.is_dispatchable);

  // Your team: confirmed, working people, per kind of work switched on.
  const staff: StaffMember[] = (team.data?.staff ?? []).filter((s) => s.confirmed && s.status === 'ACTIVE');
  const groups = PEOPLE_FOR.filter((g) => g.services.some((sv) => activeServices.includes(sv))).map((g) => {
    const mine = staff.filter((s) => g.types.includes(s.staff_type));
    return { ...g, total: mine.length, onDuty: mine.filter((s) => s.on_duty).length };
  });
  const dutyWords = groups
    .filter((g) => g.onDuty > 0)
    .map((g) => `${g.onDuty} ${g.label.toLowerCase().split(' ')[0]}`)
    .join(', ');

  // Needs your attention.
  const attention: Attention[] = [];
  const overdue = c.overdue_to_staff ?? 0;
  if (overdue > 0)
    attention.push({ key: 'overdue', tone: 'danger', text: `${overdue} job${overdue === 1 ? '' : 's'} won and past the time to staff`, sub: 'The family is waiting. Assign someone now.', action: 'Assign', to: '/assign' });
  if (c.to_staff - overdue > 0)
    attention.push({ key: 'to_staff', tone: 'amber', text: `${c.to_staff - overdue} job${c.to_staff - overdue === 1 ? '' : 's'} won — choose who goes`, action: 'Assign', to: '/assign' });
  for (const u of ov.data?.upcoming ?? []) {
    const startsIn = u.start_utc ? (new Date(u.start_utc).getTime() - now) / 3600_000 : null;
    const waiting = u.people.filter((p) => !p.accepted);
    if (waiting.length && startsIn !== null && startsIn < UNACCEPTED_WARN_HOURS)
      attention.push({
        key: `unaccepted:${u.shift_public_id ?? u.item_public_id}`,
        tone: startsIn < 1 ? 'danger' : 'amber',
        text: `${waiting.map((p) => p.name || 'Your staff member').join(', ')} has not accepted yet`,
        sub: `${serviceName(u.service_code)} · ${whenWords(u.start_utc, now)}`,
        action: 'Open',
        to: `/assign/${u.item_public_id}`,
      });
  }
  for (const n of notices.data ?? []) {
    if ((n.kind !== 'ASSIGNMENT_DECLINED' && n.kind !== 'STAFF_OFFER_EXPIRED') || now - Date.parse(n.created_at) > 24 * 3600_000) continue;
    attention.push({ key: `notice:${n.public_id}`, tone: 'amber', text: n.title, sub: `${n.body} · ${ago(n.created_at)}`, action: 'Assign', to: '/assign' });
  }
  if (comp.data) {
    attention.push(...paperAttention(comp.data, now));
    // Wanted papers that do not stop requests (the blocking ones keep the page
    // in its setup view): one line, so they are not forgotten.
    const wanted = missingPapers(comp.data).filter((p) => !p.blocks_dispatch);
    if (wanted.length) {
      const names = new Map(comp.data.document_types.map((t) => [t.code, t.name]));
      const list = [...new Set(wanted.map((p) => names.get(p.document_type_code) ?? p.document_type_code))];
      attention.push({ key: 'wanted-papers', tone: 'amber', text: `${list.length} paper${list.length === 1 ? '' : 's'} still to upload`, sub: `${list.join(', ')} — needed before the platform can pay you.`, action: 'Upload', to: '/documents' });
    }
  }
  if (c.join_requests > 0)
    attention.push({ key: 'join', tone: 'amber', text: `${c.join_requests} ${c.join_requests === 1 ? 'person is' : 'people are'} asking to join`, sub: 'Confirm the people who work for you.', action: 'Team', to: '/team' });
  for (const e of blockedServices)
    attention.push({
      key: `blocked:${e.service_code}`,
      tone: 'danger',
      text: `Requests cannot reach you for ${serviceName(e.service_code)}`,
      sub: BLOCKED_WORDS[e.blocked_reason ?? ''] ?? (e.blocked_reason ?? '').toLowerCase().replace(/_/g, ' '),
      action: 'See why',
      to: '/documents',
    });
  for (const g of groups)
    if (g.total > 0 && g.onDuty === 0)
      attention.push({ key: `duty:${g.key}`, tone: 'amber', text: `No ${g.label.toLowerCase()} on duty`, sub: 'A request for them cannot be staffed right now.', action: 'Team', to: '/team' });
  // 2026-10-23: a family picks an agency by its own price, so a type with no
  // price is a type nobody can pick this agency for (screens/prices.tsx).
  const unpriced = (prices.data?.items ?? []).filter((i) => !i.price);
  if (unpriced.length > 0)
    attention.push({
      key: 'unpriced',
      tone: 'amber',
      text:
        unpriced.length === 1
          ? `No price set for ${unpriced[0]?.variant_name ?? 'one type'} — families can't pick you`
          : `No price set for ${unpriced.length} types — families can't pick you for them`,
      sub: unpriced.slice(0, 4).map((i) => `${i.variant_name} (${i.service_name || serviceName(i.service_code)})`).join(', ') + (unpriced.length > 4 ? ', …' : ''),
      action: 'Set prices',
      to: '/prices',
    });

  const live = ov.data?.live ?? [];
  const liveNow = live.filter((j) => j.item_status === 'IN_PROGRESS' || j.at_work.length > 0).length;

  const week = ov.data?.week;
  const answered = week ? week.accepted + week.declined + week.missed + week.taken_by_others : 0;

  return (
    <Stack>
      {c.open_offers === 0 ? (
        <Note>
          Quiet right now — no request is open.{dutyWords ? ` On duty: ${dutyWords}.` : ' Nobody in your team is on duty.'} Keep this tab open: a chime plays when a
          request arrives.
        </Note>
      ) : null}

      {kpis}

      {c.open_offers > 0 ? closingSoonest : null}

      {/* Live jobs */}
      <Card>
        <CardHead
          title="Live jobs"
          sub="Jobs your people are on now, and the ones staffed and about to start."
          right={
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {liveNow ? <Tag fg="var(--ok)" bg="var(--ok-bg)">{liveNow} under way</Tag> : null}
              <Btn size="sm" onClick={() => navigate('/live')}>
                Live jobs
              </Btn>
            </span>
          }
        />
        {ov.error ? (
          <div style={{ padding: '0 18px 18px' }}>
            <Note tone="danger">Your live jobs could not be read: {ov.error}</Note>
          </div>
        ) : !ov.data ? (
          <Empty>Reading…</Empty>
        ) : live.length === 0 ? (
          <Empty>No job is under way or staffed right now. Jobs you win and assign will show here.</Empty>
        ) : (
          <div>
            {live.slice(0, 8).map((j: LiveJob) => {
              const underWay = j.item_status === 'IN_PROGRESS' || j.at_work.length > 0;
              const who = underWay && j.at_work.length ? j.at_work : j.next;
              return (
                <button
                  key={j.item_public_id}
                  type="button"
                  onClick={() => navigate(`/assign/${encodeURIComponent(j.item_public_id)}`)}
                  style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 12, padding: '11px 18px', border: 0, borderTop: '1px solid var(--line-soft)', background: 'transparent', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                >
                  <span style={{ width: 150, flex: 'none', font: '700 12.5px var(--font-mono)', color: underWay ? 'var(--ok)' : 'var(--ink)' }}>
                    {liveWhen(j, now)}
                  </span>
                  <span className={uiCss.grow}>
                    <span style={{ display: 'block', font: '700 13px var(--font-ui)', color: 'var(--ink)' }}>
                      <ServiceDot code={j.service_code} />
                      {serviceName(j.service_code)}
                      {j.consult_mode ? ` · ${CONSULT_WORD[j.consult_mode] ?? j.consult_mode.toLowerCase()}` : ''}
                      {j.vehicle ? ` · ${j.vehicle}` : ''}
                    </span>
                    <span className={uiCss.sub}>
                      {who.length
                        ? who.map((p) => `${p.name || 'Unnamed'} (${roleName(p.role, j.service_code).toLowerCase()}${p.status === 'PROPOSED' ? ', not accepted yet' : ''})`).join(', ')
                        : 'Nobody on the next shift yet'}
                      {j.pincode ? ` · ${j.pincode}` : ''}
                    </span>
                  </span>
                  {underWay ? (
                    <Tag fg="var(--ok)" bg="var(--ok-bg)">At work</Tag>
                  ) : who.length && who.every((p) => p.status !== 'PROPOSED') ? (
                    <Tag fg="var(--teal)" bg="var(--teal-bg)">Ready</Tag>
                  ) : (
                    <Tag fg="var(--amber)" bg="var(--amber-bg)">Waiting</Tag>
                  )}
                </button>
              );
            })}
            {live.length > 8 ? <div className={uiCss.sub} style={{ padding: '10px 18px' }}>And {live.length - 8} more — see Live jobs.</div> : null}
          </div>
        )}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: 14, alignItems: 'stretch' }}>
        {/* Today & tomorrow */}
        <Card style={FILL}>
          <CardHead title="Today & tomorrow" sub="The work you have won, by time, and who is going." right={<Btn size="sm" onClick={() => navigate('/assign')}>All jobs</Btn>} />
          {ov.error ? (
            <div style={{ padding: '0 18px 18px' }}>
              <Note tone="danger">Your jobs could not be read: {ov.error}</Note>
            </div>
          ) : !ov.data ? (
            <Empty>Reading…</Empty>
          ) : ov.data.upcoming.length === 0 ? (
            <Centered><Empty>Nothing booked for today or tomorrow yet.</Empty></Centered>
          ) : (
            <div>
              {ov.data.upcoming.slice(0, 10).map((u: UpcomingJob) => (
                <button
                  key={u.shift_public_id ?? u.item_public_id}
                  type="button"
                  onClick={() => navigate(`/assign/${u.item_public_id}`)}
                  style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 12, padding: '11px 18px', border: 0, borderTop: '1px solid var(--line-soft)', background: 'transparent', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                >
                  <span style={{ width: 128, flex: 'none', font: '700 12.5px var(--font-mono)', color: 'var(--ink)' }}>{whenWords(u.start_utc, now)}</span>
                  <span className={uiCss.grow}>
                    <span style={{ display: 'block', font: '700 13px var(--font-ui)', color: 'var(--ink)' }}>
                      <ServiceDot code={u.service_code} />
                      {serviceName(u.service_code)}
                      {u.consult_mode ? ` · ${CONSULT_WORD[u.consult_mode] ?? u.consult_mode.toLowerCase()}` : ''}
                      {u.vehicle ? ` · ${u.vehicle}` : ''}
                    </span>
                    <span className={uiCss.sub}>
                      {u.people.length
                        ? u.people.map((p) => `${p.name || 'Unnamed'}${p.accepted ? '' : ' (not accepted yet)'}`).join(', ')
                        : 'Nobody assigned yet'}
                      {u.pincode ? ` · ${u.pincode}` : ''}
                    </span>
                  </span>
                  {u.people.length === 0 ? (
                    <Tag fg="var(--amber)" bg="var(--amber-bg)">Assign</Tag>
                  ) : u.people.every((p) => p.accepted) ? (
                    <Tag fg="var(--ok)" bg="var(--ok-bg)">Ready</Tag>
                  ) : (
                    <Tag fg="var(--amber)" bg="var(--amber-bg)">Waiting</Tag>
                  )}
                </button>
              ))}
              {ov.data.upcoming.length > 10 ? <div className={uiCss.sub} style={{ padding: '10px 18px' }}>And {ov.data.upcoming.length - 10} more — see Assign staff.</div> : null}
            </div>
          )}
        </Card>

        {/* Needs your attention */}
        <Card style={FILL}>
          <CardHead
            title="Needs your attention"
            sub="Everything waiting on someone at your agency."
            right={attention.length ? <Tag fg="var(--amber)" bg="var(--amber-bg)">{attention.length}</Tag> : undefined}
          />
          {attention.length === 0 ? (
            <Centered><AllClear title="Nothing needs you right now" sub="Jobs are staffed, papers are in date and nobody is waiting to join." /></Centered>
          ) : (
            <div>
              {attention.slice(0, 8).map((x) => (
                <div key={x.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', borderTop: '1px solid var(--line-soft)' }}>
                  <span
                    aria-hidden="true"
                    style={{ width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center', flex: 'none', font: '800 12px var(--font-ui)', background: x.tone === 'danger' ? 'var(--danger-bg)' : 'var(--amber-bg)', color: x.tone === 'danger' ? 'var(--danger)' : 'var(--amber)' }}
                  >
                    !
                  </span>
                  <span className={uiCss.grow}>
                    <span style={{ display: 'block', font: '700 13px var(--font-ui)', color: 'var(--ink)' }}>{x.text}</span>
                    {x.sub ? <span className={uiCss.sub}>{x.sub}</span> : null}
                  </span>
                  <Btn size="sm" onClick={() => navigate(x.to)}>
                    {x.action}
                  </Btn>
                </div>
              ))}
              {attention.length > 8 ? <div className={uiCss.sub} style={{ padding: '10px 18px' }}>And {attention.length - 8} more.</div> : null}
            </div>
          )}
        </Card>

        {/* Your team right now */}
        <Card style={FILL}>
          <CardHead title="Your team right now" sub="Confirmed people on duty, for the work you offer." right={<Btn size="sm" onClick={() => navigate('/team')}>Team</Btn>} />
          {team.error ? (
            <div style={{ padding: '0 18px 18px' }}>
              <Note tone="danger">Your team could not be read: {team.error}</Note>
            </div>
          ) : !team.data ? (
            <Empty>Reading…</Empty>
          ) : groups.length === 0 ? (
            <Centered><Empty>Switch on a service to see who can take it.</Empty></Centered>
          ) : (
            <div>
              {groups.map((g) => (
                <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderTop: '1px solid var(--line-soft)' }}>
                  <span className={uiCss.grow} style={{ font: '700 13px var(--font-ui)', color: 'var(--ink)' }}>
                    {g.label}
                  </span>
                  <span style={{ font: '700 13px var(--font-mono)', color: g.onDuty ? 'var(--ok)' : 'var(--amber)' }}>
                    {g.onDuty} on duty
                  </span>
                  <span className={uiCss.sub} style={{ width: 64, textAlign: 'right' }}>
                    of {g.total}
                  </span>
                </div>
              ))}
              <div className={uiCss.sub} style={{ padding: '10px 18px' }}>
                People switch themselves on duty in the partner app.
              </div>
            </div>
          )}
        </Card>

        {/* This week */}
        <Card style={FILL}>
          <CardHead title="This week" sub="The last seven days." right={<Btn size="sm" onClick={() => navigate('/earnings')}>Earnings</Btn>} />
          {ov.error ? (
            <div style={{ padding: '0 18px 18px' }}>
              <Note tone="danger">{ov.error}</Note>
            </div>
          ) : !week ? (
            <Empty>Reading…</Empty>
          ) : (
            <div>
              {[
                { label: 'Requests that reached you', value: String(week.received) },
                { label: 'You accepted', value: String(week.accepted), note: answered ? `${Math.round((week.accepted / answered) * 100)}% of those closed` : undefined, color: 'var(--ok)' },
                { label: 'Someone else accepted first', value: String(week.taken_by_others), note: 'be quicker to accept' },
                { label: 'Timer ran out', value: String(week.missed), color: week.missed ? 'var(--amber)' : undefined },
                { label: 'You declined', value: String(week.declined) },
                { label: 'Jobs completed', value: String(week.completed) },
                { label: 'Earned this month', value: money$.data ? money(money$.data.summary.this_month) : '—' },
              ].map((r) => (
                <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', borderTop: '1px solid var(--line-soft)' }}>
                  <span className={uiCss.grow}>
                    <span style={{ display: 'block', font: '600 13px var(--font-ui)', color: 'var(--ink)' }}>{r.label}</span>
                    {r.note ? <span className={uiCss.sub}>{r.note}</span> : null}
                  </span>
                  <span style={{ font: '700 14px var(--font-mono)', color: r.color ?? 'var(--ink)' }}>{r.value}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Recent activity */}
      <Card>
        <CardHead title="Recent activity" sub="The last things the platform told your agency." />
        {notices.error ? (
          <div style={{ padding: '0 18px 18px' }}>
            <Note tone="danger">{notices.error}</Note>
          </div>
        ) : !notices.data ? (
          <Empty>Reading…</Empty>
        ) : notices.data.length === 0 ? (
          <Empty>Nothing yet. Requests, wins, cancellations and payments will appear here.</Empty>
        ) : (
          <div>
            {notices.data.slice(0, 6).map((n) => {
              const to = KIND_ROUTE[n.kind];
              return (
                <button
                  key={n.public_id}
                  type="button"
                  disabled={!to}
                  onClick={() => to && navigate(to)}
                  style={{ display: 'flex', width: '100%', alignItems: 'baseline', gap: 12, padding: '10px 18px', border: 0, borderTop: '1px solid var(--line-soft)', background: 'transparent', textAlign: 'left', cursor: to ? 'pointer' : 'default', font: 'inherit', color: 'inherit' }}
                >
                  <span style={{ width: 96, flex: 'none' }} className={uiCss.sub}>
                    {ago(n.created_at)}
                  </span>
                  <span className={uiCss.grow}>
                    <span style={{ display: 'block', font: '700 13px var(--font-ui)', color: 'var(--ink)' }}>{n.title}</span>
                    <span className={uiCss.sub}>{n.body}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Card>
    </Stack>
  );
}

